import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listSandboxGroups, mapApiSandboxGroupToUi } from '../../../services/sandbox.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * A record's Sandbox groups for the chip, filters and search (CT-5718). Race
 * guard, serialized keys, memoized return; the server narrows. `patchClaim`
 * lets Confirm / Send update a row optimistically and roll it back.
 */
export function useSandboxGroups({ entityType, entityId, orderBy, orderDirection, quickFilter = 'all', filters, search } = {}) {
  const [groups, setGroups] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
      }
      try {
        const parsedSearch = JSON.parse(searchKey);
        const { body } = await listSandboxGroups({
          entityType,
          entityId,
          orderBy,
          orderDirection,
          quickFilter,
          filters: JSON.parse(filtersKey),
          ...(parsedSearch ? { search: parsedSearch } : {}),
        });
        if (requestId !== fetchIdRef.current) return;
        setGroups((body?.groups ?? []).map(mapApiSandboxGroupToUi));
        setSourceOptions((body?.source_options ?? []).map((option) => ({ value: option.value, label: option.label })));
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setGroups([]);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? 'Failed to load the Sandbox');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId, orderBy, orderDirection, quickFilter, filtersKey, searchKey],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);
  const patchClaim = useCallback(
    (claimId, patch) =>
      setGroups((current) =>
        current.map((group) =>
          group.claims.some((row) => row.id === claimId)
            ? { ...group, claims: group.claims.map((row) => (row.id === claimId ? { ...row, ...patch } : row)) }
            : group,
        ),
      ),
    [],
  );

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ groups, sourceOptions, loading, error, accessDenied: status === 403, refetch, refresh, patchClaim }),
    [groups, sourceOptions, loading, error, status, refetch, refresh, patchClaim],
  );
}

export default useSandboxGroups;
